import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { LegRows } from './leg-rows';
import { StatusDot, Tag } from './status-badge';
import { groupByArea, pick, type AccountOption, type EventCoverage, type Lang, type PostingCatalog } from './model';

interface EventsTabProps {
  catalog: PostingCatalog;
  lang: Lang;
  events: EventCoverage[];
  selectedEvent: string | null;
  onSelect: (event: string) => void;
  accountOptions: AccountOption[];
  canEdit: boolean;
  busyKey: string | null;
  onSave: (event: string, role: string, dimension: string, accountId: string) => void;
  onRemove: (ruleId: string) => void;
}

export function EventsTab({ catalog, lang, events, selectedEvent, onSelect, accountOptions, canEdit, busyKey, onSave, onRemove }: EventsTabProps) {
  const { t } = useTranslation('postingRules');
  const groups = groupByArea(events, catalog);
  const current = events.find((e) => e.event === selectedEvent) ?? events[0] ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      {/* Event list */}
      <nav className="border border-border bg-card">
        {groups.map(({ area, events: areaEvents }) => (
          <div key={area.key}>
            <div className="border-b border-border bg-muted/40 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {pick(area.label, lang)}
            </div>
            {areaEvents.map((ev) => {
              const active = current?.event === ev.event;
              return (
                <button
                  key={ev.event}
                  type="button"
                  onClick={() => onSelect(ev.event)}
                  className={cn(
                    'flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50',
                    active && 'bg-muted/70 font-medium'
                  )}
                >
                  <StatusDot status={ev.status} />
                  <span className="flex-1 truncate">{pick(ev.label, lang)}</span>
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Event detail */}
      {current ? (
        <div className="space-y-4">
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <StatusDot status={current.status} />
              <h2 className="text-lg font-semibold">{pick(current.label, lang)}</h2>
              <code className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{current.event}</code>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{pick(current.description, lang)}</p>
            {current.multi && <p className="mt-1 text-xs text-muted-foreground">{t('events.multiNote')}</p>}
          </div>

          {current.legs.map((leg) => {
            const roleDef = catalog.roles.find((r) => r.role === leg.role);
            return (
              <div key={`${current.event}-${leg.role}`} className="border border-border bg-card">
                <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
                  <span
                    className={cn(
                      'rounded-md px-1.5 py-0.5 font-mono text-[11px] font-semibold',
                      leg.side === 'DR' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' : leg.side === 'CR' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {leg.side === 'DR' ? t('leg.debit') : leg.side === 'CR' ? t('leg.credit') : t('leg.either')}
                  </span>
                  <span className="font-medium">{pick(leg.label, lang)}</span>
                  <span className="text-xs text-muted-foreground">— {roleDef ? pick(roleDef.label, lang) : leg.role}</span>
                  {leg.party && <Tag>{t(`leg.party.${leg.party}`)}</Tag>}
                  {leg.note && <span className="text-xs text-muted-foreground">({pick(leg.note, lang)})</span>}
                </div>
                {roleDef && <p className="px-3 pt-2 text-xs text-muted-foreground">{pick(roleDef.description, lang)}</p>}
                <LegRows
                  catalog={catalog}
                  lang={lang}
                  event={current.event}
                  role={leg.role}
                  dimensions={leg.dimensions}
                  rows={leg.rows}
                  accountOptions={accountOptions}
                  canEdit={canEdit}
                  busyKey={busyKey}
                  onSave={(dimension, accountId) => onSave(current.event, leg.role, dimension, accountId)}
                  onRemove={onRemove}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border border-border bg-card p-8 text-center text-sm text-muted-foreground">{t('events.none')}</div>
      )}
    </div>
  );
}
